import { BuildingStorefrontIcon, CheckBadgeIcon, ChevronRightIcon, CreditCardIcon, MapPinIcon, PencilSquareIcon, PlusIcon, TruckIcon, TrashIcon } from '@heroicons/react/24/outline'
import { CheckoutOrderDetails } from '../components/checkout-order-details'
import React, { useCallback, useEffect, useState } from 'react'
import { Accordion, Alert, Button, Card, Col, Container, Form, Placeholder, Row, Spinner } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { CartItem, useCart } from '../context/cart-context'
import { AppModal } from '../components/modal'
import { ShippingAddress } from '../components/shipping-address';
import DeliveryLocation from '../components/delivery-location'
import { formatPhoneNumber } from '../helper/utils'
import PaystackPaymentService from '../services/paystack-payment-service'
import { iDeliveryLocation, LocalCurrency, PaystackTransaction, Store } from '../types'
import eventBus from '../events/event-bus'
import { useStores } from '../hooks/use-stores'
import { PlaceholderLoader } from '../components/placeholder'
import { OrderCheckoutFactory } from '../factory/order-checkout-factory'
import { OrderItemCheckoutData, ShippingAddressData } from '../data/order-check-data'
import { useOrder } from '../hooks/use-order';
import { useBrowserInfo } from '../hooks/use-browser-info';
import { usePayment } from '../hooks/use-payment';
import { useGeolocation } from '../context/geolocation-context';
import { useAuth } from '../context/auth-context'
import LocalStorage from '../helper/storage'
import { useAnalytics } from '../hooks/use-analytics'

type PaymentMethodType = {
    label: string;
    longText: string;
    showPaymentButton: boolean;
    selectedOption?: boolean;
}

const Checkout = () => {

    const { cartItems, cartTotal, clearCart } = useCart();
    const { isAuth } = useAuth();
    const { trackCheckoutStep, trackPurchase } = useAnalytics();
    const { createOrder, createdOrder, loading: createOrderLoading, error } = useOrder();
    const { payment, verifyPayment, error:paymentError, loading: paymentLoading } = usePayment();
    const { stores: allStore, loading:storeLoading } = useStores();
    const { browserInfo } = useBrowserInfo();
    const { deliveryLocations, saveUserLocationAddress, findClosestStoreToCustomer, handleFetchLocationFromAddress } = useGeolocation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSavingShippingContact, setIsSavingShippingContact] = useState<boolean>(false);
    const [isShowModal, setIsShowModal] = useState<boolean>(false);
    const [shouldAddShipping, setShouldAddShipping] = useState<boolean>(true);
    const [editSavedLocation, setEditSavedLocation] = useState<iDeliveryLocation>({});
    const [shippingAddresses, setShippingAddresses] = useState<iDeliveryLocation[]>(deliveryLocations);
    const [selectedDeliveryLocation, setSelectedDeliveryLocation] = useState<iDeliveryLocation>(deliveryLocations[0]);
    
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<Store|undefined>();
    const [shippingCharge, setShippingCharge] = useState<number>(0);
    const [extraShippingCharge, setExtraShippingCharge] = useState<number>(0);
    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const [errors, setErrors] = useState<string>('');

    
    useEffect(() => {

        if (allStore.length === 0 || cartItems.length === 0 || !selectedDeliveryLocation) {
            return ;
        }


        const filteredStore = findClosestStoreToCustomer(allStore, selectedDeliveryLocation);

        if (filteredStore.length === 0) {
            return ;
        }

        const baseShippingCharges = filteredStore[0].logisticCompany.baseShippingCharges;

        // Calculate the total quantity.
        const qtySum = cartItems.reduce((sumQty, cart) => sumQty + cart.quantity, 0);

        // Divide subsequent carton shipment price from the base price into halves
        // Remove one from the total quantity and then slash the shipment price into havlves.
        const subsequentShipment = ((qtySum - 1) * (baseShippingCharges/2));
        setShippingCharge(baseShippingCharges);
        setExtraShippingCharge(subsequentShipment);
        setTotalQuantity(qtySum);
        setSelectedStore(filteredStore[0]);
        setStores(filteredStore);

    }, [allStore, cartItems, selectedDeliveryLocation]);
    

    const paymentMethods:PaymentMethodType[]  = [
        {
            label: 'Payment with Paystack',
            longText: 'You can securely make payment using your card, transfer, ussd etc. We support Mastercard, Verve, Visa.',
            showPaymentButton: true,
            selectedOption: true,
        },
        // {
        //     label: 'Bank Transfer',
        //     longText: 'We collect bank transfer for those who choose to use bank app e.t.c.',
        //     showPaymentButton: false,
        // },
    ]
        
    const handleAddressLocationChange = (location: iDeliveryLocation) => {
        setEditSavedLocation(location);
        handleModal(true);
    }

    const handlePaymentUsingPaystack = useCallback(() => {

        try {

            if (!selectedStore) {
                return ;
            }

            const defaultShippingAddress = shippingAddresses.find(addr => addr.defaults);

            if (!defaultShippingAddress?.firstName?.trim()) {
                setErrors('First name should not be empty');
                return ;
            }

            if (!defaultShippingAddress?.lastName?.trim()) {
                setErrors('Last name should not be empty');
                return ;
            }

            if (!defaultShippingAddress?.email?.trim()) {
                setErrors('Email should not be empty');
                return ;
            }


            const orderFactory = new OrderCheckoutFactory()
                .addCurrency('₦')
                .addTotalPrice(cartTotal)
                .addTotalQuantity(totalQuantity)
                .addLogisticsId(selectedStore.logisticCompany.id)
                .addStoreId(selectedStore.id)
                .addShippingMethod(shouldAddShipping ? 'drop-off' : 'pickup')
                .addOrderItems(cartItems.map(cartItem => new OrderItemCheckoutData(
                    cartItem.id, 
                    cartItem.discountPrice,
                    cartItem.currency,
                    cartItem.discountOff || '',
                    cartItem.quantity,
                    cartItem.basePrice,
                    cartItem.applyDiscount,
                    (cartItem.applyDiscount ? cartItem.discountPrice * cartItem.quantity : cartItem.basePrice * cartItem.quantity),
                )))
                .addShippingAddress(new ShippingAddressData(
                    defaultShippingAddress?.id || 0,
                    defaultShippingAddress?.firstName || '',
                    defaultShippingAddress?.lastName || '',
                    defaultShippingAddress?.address || '',
                    defaultShippingAddress?.country || 'Nigeria',
                    defaultShippingAddress?.state || '',
                    defaultShippingAddress?.email || '',
                    defaultShippingAddress?.phone || '',
                    defaultShippingAddress?.heading || '',
                    defaultShippingAddress?.defaults || true
                ))
                .addBrowserInfo(browserInfo)
                .build();

            // Track checkout step - payment initiation
            trackCheckoutStep('payment_initiated', cartTotal, totalQuantity);
            
            createOrder(orderFactory);
            
        } catch (error) {
            console.log("error handlePaymentUsingPaystack = ", error)
        }

    }, [shouldAddShipping, shippingAddresses, cartTotal, totalQuantity, selectedStore, cartItems]);


    const onSuccess = useCallback((event: Event) => {
        const customEvent = event as CustomEvent;
        const transaction = customEvent.detail as PaystackTransaction;
        verifyPayment(transaction.trxref);
    }, [])
    
    const onCancel = useCallback((event: Event) => {
        setErrors("User cancel the payment modal");
        setIsLoading(false);
    }, [])
    
    const onError = useCallback((event: Event) => {
        const customEvent = event as CustomEvent;
        setErrors(customEvent.detail.error);
        setIsLoading(false);
    }, [])


    useEffect(() => {
        if (!error) {
            return;
        }

        setErrors(error);
    }, [error])

    
    useEffect(() => {
        if (!paymentError) {
            return;
        }

        setErrors(paymentError);
    }, [paymentError])

    
    useEffect(() => {
        if (!payment) {
            return;
        }
        
        // Track successful purchase
        if (createdOrder?.orderDto?.id) {
            trackPurchase(
                createdOrder.orderDto.id.toString(),
                cartTotal,
                totalQuantity,
                'paystack'
            );
        }
        
        clearCart();
        LocalStorage.removeItem<CartItem>('cart');
        // navigate
        navigate('/order-purchased');
    }, [payment?.txRef, createdOrder?.orderDto?.id, cartTotal, totalQuantity, trackPurchase])


    useEffect(() => {

        if (deliveryLocations.length === 0) {
            return ;
        }

        setShippingAddresses(deliveryLocations);
        const defaultAddress = deliveryLocations.find(addr => addr.defaults);

        if (!defaultAddress) {
            return ;
        }

        setSelectedDeliveryLocation(defaultAddress);

    }, [deliveryLocations])
    
    useEffect(() => {

        const fetchAndFilterStores = async () => {
            if (!selectedDeliveryLocation || allStore.length === 0) {
                return;
            }
    
            let filteredStore: Store[] = [];
    
            if (selectedDeliveryLocation.latitude !== 0 && selectedDeliveryLocation.longitude !== 0) {
                filteredStore = findClosestStoreToCustomer(allStore, selectedDeliveryLocation);
            } else {
                const location = await handleFetchLocationFromAddress(selectedDeliveryLocation);
                if (location) {
                    await saveUserLocationAddress(location);
                    filteredStore = findClosestStoreToCustomer(allStore, location);
                }
            }
    
            setStores(() => filteredStore.length > 0 ? filteredStore : allStore);
        };

        void fetchAndFilterStores();

    }, [selectedDeliveryLocation?.id, allStore])


    useEffect(() => {

        if (!createdOrder || !createdOrder?.orderDto || !createdOrder?.paymentDto || !createdOrder?.totalAmountToBePaid) {
            return ;
        }

        const customer = createdOrder.orderDto.customer;
        const paymentDto = createdOrder.paymentDto;


        const email = customer.email;
        const phone = customer.phone;
        const firstName = customer.firstName;
        const lastName = customer.lastName;
        const totalAmount = createdOrder.totalAmountToBePaid;
        const txRef = paymentDto.txRef;

        try {
            const paystackSDK = new PaystackPaymentService();
    
            paystackSDK.builder()
                .addReference(txRef)
                .addApiKey(process.env.REACT_APP_PAYSTACK_PUBLIC_KEY||"")
                .addEmail(email)
                .addAmount(totalAmount)
                .addCurrency(LocalCurrency.shortcode)
                .addFirstName(firstName)
                .addLastName(lastName)
                .addPhone(phone)
                .build();
            
        } catch (error) {
            console.error("payment error: " + error);
        }



    }, [createdOrder?.paymentDto?.txRef, createdOrder?.totalAmountToBePaid]);


    useEffect(() => {

        eventBus.addEventListener('payment:onSucces', onSuccess);
        eventBus.addEventListener('payment:onCancel', onCancel);
        eventBus.addEventListener('payment:onError', onError);

        return (() => {
            eventBus.removeEventListener('payment:onSucces', onSuccess)
            eventBus.removeEventListener('payment:onCancel', onCancel)
            eventBus.removeEventListener('payment:onError', onError)
        })

    }, [])

    const handleModal = (toggle: boolean) => setIsShowModal(toggle);

    const handleLocationSetter = useCallback(async (deliveryLocation: iDeliveryLocation) => {
        
        setIsSavingShippingContact(true);
        
        const newDeliveryLocation: iDeliveryLocation = {
            latitude: 0,
            longitude: 0,
            ...deliveryLocation,
            phone: formatPhoneNumber(deliveryLocation.phone||""),
            id: new Date().getTime()
        }

        await saveUserLocationAddress(newDeliveryLocation);
        setSelectedDeliveryLocation(newDeliveryLocation);

        setTimeout(() => {
            handleModal(false);
            setIsSavingShippingContact(false);
        }, 10); 

    }, [handleModal, formatPhoneNumber]);



    const handleLocationActiveChange = useCallback(async (location: iDeliveryLocation) => {
        const newUserLocation = {...location, defaults: true};
        await saveUserLocationAddress(newUserLocation);
        setSelectedDeliveryLocation(newUserLocation);
    }, [])



    const isEdittingShippingAddress = () => Object.keys(editSavedLocation).length > 0;

    const handleSelectStore = useCallback((store: Store) => {
        setShippingCharge(store.logisticCompany.baseShippingCharges);
        setSelectedStore(store);
    }, [])

    const handleDeleteAddress = useCallback((id: number|undefined) => {
        if (!id) {
            return ;
        }

        const updatedDelivery = deliveryLocations.filter(d => Number(d.id) !== Number(id));

        LocalStorage.saveItem<iDeliveryLocation[]>('deli-locations', updatedDelivery);

        setShippingAddresses(updatedDelivery);

    }, [deliveryLocations]);

    return (
        <section id="shop my-4">
            <Container fluid>
                <Row className="bg-light justify-content-start mb-5">
                    <h1 className="section-title mt-4 mb-2 fs-2" data-aos="fade-up">CHECKOUT</h1>
                    <span style={{fontSize: '.8rem'}} className='mb-4'>
                        {
                            isAuth ? (
                                <>
                                    <Link to="/customer/dashboard" className='text-success'>Dashboard</Link> 
                                    <ChevronRightIcon width={12} height={12} className='text-success' /> 
                                </>
                            ): (
                                <>
                                    <Link to="/" className='text-success'>Home</Link> 
                                    <ChevronRightIcon width={12} height={12} className='text-success' /> 
                                    <Link to="/shop" className='text-success'>Shop</Link> 
                                    <ChevronRightIcon width={12} height={12} className='text-success' /> 
                                </>
                            )
                        }
                        <small>Checkout</small>
                    </span>
                </Row>
            </Container>
            <Container  data-aos="fade-up">
                {errors && <Alert style={{ fontSize: '.9rem' }} variant='danger'>{errors}</Alert>}
                <Row style={{ marginBottom: '80px' }}>
                    <Col xs={12} lg={7}>
                        <Accordion defaultActiveKey="0">
                            <Accordion.Item eventKey="0" className='border-bottom position-relative'>
                                <Accordion.Header className='d-flex m-0 flex-row justify-content-between align-items-end'>
                                   <p className='d-flex m-0 flex-row justify-content-between align-items-end'>
                                        <MapPinIcon width={20} height={20} color='#868686' /> 
                                        <span className='mx-2' style={{lineHeight: 1}}>Delivery address</span>
                                    </p>
                                </Accordion.Header>
                                <Button 
                                    variant='outline-success' 
                                    size='sm' 
                                    className='rounded position-absolute' 
                                    type='button'
                                    style={{
                                        top: '7.7px',
                                        right: '7px',
                                        zIndex: '99'
                                    }}
                                    onClick={() => {
                                        setEditSavedLocation({});
                                        handleModal(true)
                                    }}
                                >
                                  <PlusIcon width={20} height={20} />  Add your address
                                </Button>
                                <Accordion.Body className='py-3'>
                                    <Form>
                                        <Row>
                                            {
                                            
                                                (() => {

                                                    if (shippingAddresses.length === 0) {
                                                        return (
                                                            <h5 
                                                                style={{ 
                                                                    fontSize: '.9rem', 
                                                                    textIndent: '40px', 
                                                                    color: '#868686' 
                                                                }}
                                                            >No address setup.</h5>
                                                        )
                                                    }
                                                    
                                                    if (shippingAddresses.length > 0) {
                                                        return (shippingAddresses.map((location: iDeliveryLocation, i:number) => (
                                                            <Col xs={12} md={4} key={`shipping-address-${i}`} className='mb-3'>     
                                                                <Card>
                                                                    <Card.Body style={{ fontSize: '.9rem', color: '#868686' }}>
                                                                        <Row>
                                                                            <Col xs={10}>
                                                                                <Form.Check
                                                                                    inline
                                                                                    label={ location.heading }
                                                                                    name="group"
                                                                                    type="radio"
                                                                                    checked={selectedDeliveryLocation?.id === location.id}
                                                                                    id={`shipping-address-inline-radio-${i}`}
                                                                                    onChange={() => handleLocationActiveChange(location)}
                                                                                />
                                                                            </Col>
                                                                            <Col xs={2} className='text-right d-flex align-items-center'>
                                                                                <PencilSquareIcon 
                                                                                    width={20} 
                                                                                    height={20} 
                                                                                    className='text-muted' 
                                                                                    style={{cursor: 'pointer'}} 
                                                                                    onClick={() => handleAddressLocationChange(location)}
                                                                                />
                                                                            </Col>
                                                                            <Col xs={12}><DeliveryLocation location={location} /></Col>
                                                                            <Col xs={12} className='d-flex align-items-center justify-content-end'>
                                                                                <Link 
                                                                                    to={"#"}
                                                                                    style={{
                                                                                        color: '#f03532',
                                                                                        fontSize: '.8rem'
                                                                                    }}
                                                                                    onClick={() => handleDeleteAddress(location.id)}
                                                                                >Delete</Link>
                                                                            </Col>
                                                                        </Row>
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        )))
                                                    }


                                                    if (isSavingShippingContact) {
                                                        return (
                                                            <Col xs={12} md={4} className='mb-3'>     
                                                                <Card>
                                                                    <Card.Body style={{ fontSize: '.9rem', color: '#868686' }}>
                                                                        <Placeholder as={Card.Title} animation="glow">
                                                                            <Placeholder xs={6} />
                                                                        </Placeholder>
                                                                        <Placeholder as={Card.Text} animation="glow">
                                                                            <Placeholder xs={7} /> <Placeholder xs={4} /> <Placeholder xs={4} />{' '}
                                                                            <Placeholder xs={6} /> <Placeholder xs={8} />
                                                                        </Placeholder>
                                                                        <Placeholder.Button variant="primary" xs={6} />
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        )
                                                    }

                                                })()
                                            }
                                        </Row>
                                    </Form>
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="1" className='border-bottom position-relative'>
                                <Accordion.Header className='d-flex m-0 flex-row justify-content-between align-items-center'>
                                    <p className='d-flex m-0'>
                                        <TruckIcon width={20} height={20} color='#868686' /> 
                                        <span className='mx-2' style={{lineHeight: 1}}>Delivery instructions</span>
                                    </p>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Row className='px-4 my-2 d-flex align-items-center'>
                                        <Col xs={7} className='p-0'>
                                            <Form.Check
                                                inline
                                                label="I want my order delivered to the above address?"
                                                name="group"
                                                type="radio"
                                                defaultChecked={true}
                                                id={`inline-store-2`}
                                                onChange={() => setShouldAddShipping(true)}
                                                style={{ fontSize: '.9rem' }}
                                            />
                                        </Col>
                                        <Col xs={5} className='p-0'>
                                            <Form.Check
                                                inline
                                                label="I want to pickup order from store?"
                                                name="group"
                                                type="radio"
                                                id={`inline-store-1`}
                                                onChange={() => setShouldAddShipping(false)}
                                                style={{ fontSize: '.9rem' }}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className='px-4 my-2 d-flex align-items-center'>
                                        <Col xs={12}>
                                            {
                                                storeLoading ? <PlaceholderLoader /> : 
                                                stores.map((store:Store, i:number) => (
                                                    <Card key={`store-card-${i}`} className='my-2'>
                                                        <Card.Body className='position-relative d-flex justify-content-between py-2'>
                                                            <div className="store-info text-black">
                                                                <Form.Check
                                                                    inline
                                                                    name="stores"
                                                                    type="radio"
                                                                    label={<p className='m-0 text-black' style={{fontWeight: 500, fontSize: '1rem'}}>{store.name} <CheckBadgeIcon fill='#fff' width={20} height={20} color='#198754' /></p>}
                                                                    checked={selectedStore?.id === store.id}
                                                                    id={`inline-stores-${i}`}
                                                                    className='py-1 m-0'
                                                                    style={{ fontSize: '.9rem' }}
                                                                    onChange={() => handleSelectStore(store)}
                                                                />
                                                                <p className='m-0' style={{color: '#868686', fontSize: '.9rem', textIndent: '23px'}}>{store.address}</p>
                                                                <p className='m-0' style={{color: '#868686', fontSize: '.7rem', textIndent: '23px'}}>These are the closest store location to you.</p>
                                                            </div>
                                                            <div><BuildingStorefrontIcon width={20} height={20} color='#868686' /></div>
                                                        </Card.Body>
                                                    </Card>
                                                ))
                                            }
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="2" className='border-bottom position-relative mb-3'>
                                <Accordion.Header className='d-flex m-0 flex-row justify-content-between align-items-center'>
                                    <p className='d-flex m-0'>
                                        <CreditCardIcon width={20} height={20} color='#868686' /> 
                                        <span className='mx-2' style={{lineHeight: 1}}>Payment Method</span>
                                    </p>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Row className='px-5 my-2 d-flex align-items-end justify-content-end'>
                                        {
                                            paymentMethods.map((paymentMethod: PaymentMethodType, i:number) => (
                                                <Col xs={12} className='p-0 mb-4' key={`payment-${i}`}>
                                                    <Card>
                                                        <Card.Body className='position-relative'>
                                                            <Form.Check
                                                                inline
                                                                name="payment"
                                                                type="radio"
                                                                label={paymentMethod.label}
                                                                defaultChecked={paymentMethod.selectedOption}
                                                                id={`inline-payment-${i}`}
                                                                style={{ fontSize: '1rem' }}
                                                            />
                                                            <div style={{
                                                                fontSize: '0.7rem',
                                                                textIndent: '26px',
                                                                marginTop: '4px'
                                                            }}>{paymentMethod.longText}</div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))
                                        }
                                        <Button 
                                            variant='danger' 
                                            size='sm' 
                                            className='rounded'
                                            style={{
                                                width: 'fit-content',
                                                padding: '5px 10px',
                                                fontSize: '.8rem',
                                            }}
                                            onClick={createOrderLoading || paymentLoading ? () => null : handlePaymentUsingPaystack}
                                        >
                                            {createOrderLoading || paymentLoading ? 
                                                <Spinner animation="border" size='sm' variant='white' /> : 
                                                <span>Place Order and make payment</span>
                                            }
                                            
                                        </Button>
                                    </Row>
                                        
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </Col>
                    <Col xs={12} lg={5}>
                        <CheckoutOrderDetails 
                            cartItems={cartItems} 
                            cartTotal={cartTotal} 
                            shippingCharge={shippingCharge} 
                            shouldAddShipping={shouldAddShipping}  
                            extraShippingCharge={extraShippingCharge}
                            totalQuantity={totalQuantity}
                        />
                    </Col>
                </Row>
            </Container>

            <AppModal 
                isShowModal={isShowModal}
                handleCloseModal={handleModal}
                title={
                    <div className='p-0 m-0 mt-2'>
                        <h3 className='m-0 p-0' style={{ fontSize: '1rem' }}>
                           { isEdittingShippingAddress() ? 'Editing Shipping Address' : 'New Shipping Address'} 
                        </h3>
                        <p className='m-0 p-0' style={{
                            color: '#868686',
                            fontSize: '.8rem'
                        }}>{ isEdittingShippingAddress() ? 'Edit' : 'Add new' } shipping address for your order delivery.</p>
                    </div>
                }
                size=''
                children={
                    <ShippingAddress 
                        payload={editSavedLocation} 
                        handleModal={handleModal} 
                        handleLocationSetter={handleLocationSetter} 
                    />
                }
            />
        </section>
    )
}

export default Checkout
